/**
 * Derivation hook for the dashboard filter panel — view-scope narrowing, the
 * cascading country/site/warehouse option lists, the division/site scope locks,
 * and the quick-date + country change handlers.
 *
 * Extracted from DashboardFilters.tsx to keep that component under the 500-line
 * limit (CR-6352 review); the component now consumes the values returned here
 * and only renders JSX. Behaviour is unchanged — logic was moved verbatim.
 */
import { useEffect, useRef, useMemo } from 'react';
import { useView } from '../../contexts/ViewContext';
import { usePortalMode } from '../../hooks/usePortalMode';
import {
  type FilterState,
  computeQuickDateRange,
  getQuickRangeDates,
  deriveCountryOptions,
} from './dashboardFilterUtils';

type SiteOption = { id: number; name: string; country?: string; group_id?: number | null; group?: number | null };
type SupplierOption = { id: number; name: string; group?: number | null; country?: string | null };
type WarehouseOption = { id: number; name: string; site: number };
type DivisionOption = { id: number; name: string };

interface UseDashboardFiltersArgs {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  sites: SiteOption[];
  suppliers: SupplierOption[];
  warehouses: WarehouseOption[];
  divisions: DivisionOption[];
}

/**
 * Derive the dashboard filter panel's option lists, scope locks and change
 * handlers from the current filters + view context.
 *
 * @param args - The current filter state and setter plus the raw option data.
 * @param args.filters - Current filter values.
 * @param args.setFilters - Commits an updated filter set (whole-object setter).
 * @param args.sites - All accessible company sites.
 * @param args.suppliers - All accessible suppliers (carry group + country).
 * @param args.warehouses - All accessible warehouses.
 * @param args.divisions - Division (mid-level group) options.
 * @returns The view-scoped option lists (sites, warehouses, country options,
 *   divisions), the division/site scope-lock flags + locked division value,
 *   `portalIsSupplier`, the active quick-range flags, and the quick-date /
 *   company-country / supplier-country change handlers the panel renders.
 */
export function useDashboardFilters({
  filters, setFilters, sites, suppliers, warehouses, divisions,
}: UseDashboardFiltersArgs) {
  // CR-4577: Filter site/company options based on view context
  const { viewLevel, selectedCompanyId, selectedSiteId, selectedGroupId } = useView();
  // CR-4887: For supplier users the view context refers to the SUPPLIER's own
  // hierarchy, while the Company Site / Division dropdowns show CUSTOMER
  // hierarchy. Bypass the scope-narrowing/lock logic for suppliers so the
  // dropdowns stay user-driven.
  const { isSupplier: portalIsSupplier } = usePortalMode();

  // CR-6276: at SITE level a site-restricted user has selectedCompanyId === null;
  // ViewContext pins the active site in selectedSiteId instead. selectedSiteId is
  // authoritative at SITE level; at COMPANY level both are the same id.
  const activeSiteId = selectedSiteId ?? selectedCompanyId;

  const viewFilteredSites = useMemo(() => {
    if (portalIsSupplier) return sites;
    if (viewLevel === 'SITE' && activeSiteId) {
      return sites.filter((s) => s.id === activeSiteId);
    }
    if (viewLevel === 'COMPANY' && activeSiteId) {
      return sites.filter((s) => s.id === activeSiteId);
    }
    if (viewLevel === 'GROUP' && selectedGroupId && selectedGroupId > 0) {
      // Filter to sites within the selected group (division level).
      // Check both group_id (SiteSerializer) and group (CompanyListSerializer).
      const inGroup = sites.filter((s) => (s.group_id ?? s.group) === selectedGroupId);
      // If no direct matches, it's a root/parent group — show all sites.
      return inGroup.length > 0 ? inGroup : sites;
    }
    return sites;
  }, [sites, viewLevel, activeSiteId, selectedGroupId, portalIsSupplier]);

  // CR-4577 rework: divisions honour the current view scope so the dropdown
  // can't offer a division that isn't in the active scope.
  const scopedDivisions = useMemo(() => {
    if (portalIsSupplier) return divisions;
    if (viewLevel === 'GROUP' && selectedGroupId && selectedGroupId > 0) {
      const match = divisions.filter((d) => d.id === selectedGroupId);
      return match.length > 0 ? match : divisions;
    }
    if ((viewLevel === 'SITE' || viewLevel === 'COMPANY') && activeSiteId) {
      const site = sites.find((s) => s.id === activeSiteId);
      const parentGroupId = site?.group_id ?? site?.group;
      if (parentGroupId) {
        const match = divisions.filter((d) => d.id === parentGroupId);
        if (match.length > 0) return match;
      }
    }
    return divisions;
  }, [divisions, viewLevel, selectedGroupId, activeSiteId, sites, portalIsSupplier]);

  // Locked when scope has pinned us to a single option — the filter is disabled
  // in the UI AND its value is synced to the scope by the effect below.
  const isDivisionLocked = !portalIsSupplier && scopedDivisions.length === 1
    && ((viewLevel === 'GROUP' && !!selectedGroupId && selectedGroupId > 0)
      || ((viewLevel === 'SITE' || viewLevel === 'COMPANY') && !!activeSiteId));
  const isSiteLocked = !portalIsSupplier && (viewLevel === 'SITE' || viewLevel === 'COMPANY') && !!activeSiteId;
  const lockedDivisionValue = isDivisionLocked ? String(scopedDivisions[0].id) : '';
  const lockedSiteValue = isSiteLocked && activeSiteId ? String(activeSiteId) : '';

  // Sync filter values to the scope lock (and clear on the locked → unlocked
  // transition) so a stale division/site value can't linger and silently narrow
  // downstream options. Clearing only fires on the transition (tracked via refs)
  // so a manual pick at an unlocked level survives until the next scope change.
  const prevSiteLockedRef = useRef(isSiteLocked);
  const prevDivisionLockedRef = useRef(isDivisionLocked);
  useEffect(() => {
    const updates: Partial<FilterState> = {};
    const wasDivisionLocked = prevDivisionLockedRef.current;
    const wasSiteLocked = prevSiteLockedRef.current;
    prevDivisionLockedRef.current = isDivisionLocked;
    prevSiteLockedRef.current = isSiteLocked;

    if (isDivisionLocked) {
      if (filters.division !== lockedDivisionValue) {
        updates.division = lockedDivisionValue;
      }
    } else if (wasDivisionLocked && filters.division) {
      updates.division = '';
    }

    if (isSiteLocked) {
      if (filters.site !== lockedSiteValue) {
        updates.site = lockedSiteValue;
      }
    } else if (wasSiteLocked && filters.site) {
      updates.site = '';
    }

    if (Object.keys(updates).length > 0) {
      setFilters({ ...filters, ...updates });
    }
    // `filters`/`setFilters` ARE in the dep list so the lock re-asserts itself
    // after an external reset that clears site/division while a scope lock is
    // still active. The effect is idempotent (it only writes when a value is
    // out of sync), so depending on the whole `filters` object can't loop.
  }, [isDivisionLocked, isSiteLocked, lockedDivisionValue, lockedSiteValue, filters, setFilters]);

  // Filter changes are NOT auto-applied — the dashboard refetches only when the
  // Apply button calls onApplyFilters.
  const setQuickDateRange = (range: string) => {
    const result = computeQuickDateRange(range);
    if (!result) return;
    setFilters({ ...filters, dateFrom: result.dateFrom, dateTo: result.dateTo, dataMode: result.dataMode });
  };

  const activeQuickRange = ['last12Months', 'thisMonth', 'lastMonth', 'lastQuarter', 'thisYear', 'lastYear']
    .find((range) => {
      const dates = getQuickRangeDates(range);
      return dates && filters.dateFrom === dates.dateFrom && filters.dateTo === dates.dateTo;
    }) || null;

  const isLast12MonthsActive = activeQuickRange === 'last12Months';

  // Company Country options — derived from view-filtered sites (ISO codes).
  const siteCountryOptions = deriveCountryOptions(viewFilteredSites);

  // Filter sites by view context + selected Company Country.
  const filteredSites = filters.siteCountry
    ? viewFilteredSites.filter((s) => s.country === filters.siteCountry)
    : viewFilteredSites;

  // When Company Country changes, clear site + warehouse if they no longer match.
  const handleSiteCountryChange = (country: string) => {
    const updates: Partial<FilterState> = { siteCountry: country };
    if (country && filters.site) {
      const selectedSite = sites.find((s) => String(s.id) === filters.site);
      if (selectedSite && selectedSite.country !== country) {
        updates.site = '';
        updates.warehouse = '';
      }
    }
    setFilters({ ...filters, ...updates });
  };

  // CR-6352: Supplier Country options come from the actual supplier data
  // (free-text country names) — not a hardcoded ISO list, which never matched
  // the backend supplier_country filter.
  const supplierCountryOptions = deriveCountryOptions(suppliers);

  // CR-6352: Supplier Country acts as a parent filter for Supplier Site.
  // Changing it clears a selected supplier that no longer belongs to the chosen
  // country so the dashboard never reads an inconsistent combo.
  const handleSupplierCountryChange = (country: string) => {
    const updates: Partial<FilterState> = { supplierCountry: country };
    if (country && filters.supplier) {
      const selectedSupplier = suppliers.find((s) => String(s.id) === filters.supplier);
      if (selectedSupplier && selectedSupplier.country !== country) {
        updates.supplier = '';
      }
    }
    setFilters({ ...filters, ...updates });
  };

  // Filter warehouses by view context, selected site, and/or Company Country.
  const filteredWarehouses = warehouses.filter((wh) => {
    const siteInScope = viewFilteredSites.some((s) => s.id === wh.site);
    if (!siteInScope) return false;
    if (filters.site && String(wh.site) !== filters.site) return false;
    if (filters.siteCountry) {
      const whSite = sites.find((s) => s.id === wh.site);
      if (whSite && whSite.country !== filters.siteCountry) return false;
    }
    return true;
  });

  return {
    portalIsSupplier,
    scopedDivisions,
    isDivisionLocked,
    isSiteLocked,
    lockedDivisionValue,
    siteCountryOptions,
    supplierCountryOptions,
    filteredSites,
    filteredWarehouses,
    activeQuickRange,
    isLast12MonthsActive,
    setQuickDateRange,
    handleSiteCountryChange,
    handleSupplierCountryChange,
  };
}
